// Validate and probe original MP4 files before resumable Firebase uploads.
// Abort cancels metadata reads/uploads; originals are never resized or transcoded.
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../../firebase";
import { PROMOTION_VIDEO_MAX_BYTES, PROMOTION_VIDEO_MIN_SECONDS, PROMOTION_VIDEO_MAX_SECONDS, PROMOTION_VIDEO_MAX_DIMENSION } from "../constants/promotion_video";

const DEFAULTS = { maxBytes: PROMOTION_VIDEO_MAX_BYTES, minSeconds: PROMOTION_VIDEO_MIN_SECONDS, maxSeconds: PROMOTION_VIDEO_MAX_SECONDS, maxDimension: PROMOTION_VIDEO_MAX_DIMENSION };
const aborted = () => Object.assign(new Error("Video upload cancelled."), { name: "AbortError" });

export function validatePromotionVideoFile(file, constraints = DEFAULTS) {
  if (file?.type !== "video/mp4" || !/\.mp4$/i.test(file?.name || "")) throw new Error("Choose an MP4 video (.mp4, video/mp4).");
  if (!Number.isFinite(file.size) || file.size <= 0 || file.size > constraints.maxBytes) throw new Error("Choose a nonempty video no larger than 100 MiB.");
  return file;
}

export function validatePromotionVideoMetadata(metadata, constraints = DEFAULTS) {
  if (!Number.isFinite(metadata?.durationSeconds) || metadata.durationSeconds < constraints.minSeconds || metadata.durationSeconds > constraints.maxSeconds) throw new Error("Video length must be between 5 and 60 seconds.");
  if (![metadata?.width, metadata?.height].every((value) => Number.isFinite(value) && value > 0 && value <= constraints.maxDimension)) throw new Error("Video dimensions must be greater than zero and no larger than 4096 pixels per side.");
  return metadata;
}

export function probePromotionVideo(file, { signal, timeoutMs = 15000 } = {}) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(aborted()); return; }
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    let timer;
    let settled = false;
    const finish = (error, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener("abort", cancel);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
      if (error) reject(error); else resolve(value);
    };
    const cancel = () => finish(aborted());
    video.onloadedmetadata = () => finish(null, { durationSeconds: video.duration, width: video.videoWidth, height: video.videoHeight });
    video.onerror = () => finish(new Error("Could not read this MP4. Try a playable MP4 encoded with H.264 video and AAC audio."));
    signal?.addEventListener("abort", cancel, { once: true });
    timer = setTimeout(() => finish(new Error("Video metadata timed out. Try another MP4.")), timeoutMs);
    video.preload = "metadata";
    video.src = url;
  });
}

export async function uploadPromotionVideo(file, eventId = null, { constraints = DEFAULTS, signal, onProgress } = {}) {
  validatePromotionVideoFile(file, constraints);
  const metadata = validatePromotionVideoMetadata(await probePromotionVideo(file, { signal }), constraints);
  if (signal?.aborted) throw aborted();
  const filename = `${crypto.randomUUID()}.mp4`;
  const storagePath = eventId ? `eventAssets/${eventId}/promotion_videos/${filename}` : `promotionAssets/internal_videos/${filename}`;
  const objectRef = ref(storage, storagePath);
  const task = uploadBytesResumable(objectRef, file, { contentType: "video/mp4" });
  await new Promise((resolve, reject) => {
    const cancel = () => { task.cancel(); reject(aborted()); };
    const cleanup = () => signal?.removeEventListener("abort", cancel);
    signal?.addEventListener("abort", cancel, { once: true });
    task.on("state_changed", (snapshot) => onProgress?.(snapshot.totalBytes ? Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100) : 0), (error) => { cleanup(); reject(error); }, () => { cleanup(); resolve(); });
    if (signal?.aborted) cancel();
  });
  const url = await getDownloadURL(objectRef);
  if (signal?.aborted) throw aborted();
  return { url, storagePath, fileName: file.name, contentType: file.type, sizeBytes: file.size, ...metadata };
}
